import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store';
import { useCampaignsApi } from '../api';
import {
  resetError,
  setAwayCustomMessageCampaign,
  setAwayRecurringMessagesCampaign,
  setError,
  setLoading,
} from '../slices';
import { AwayMessageCampaignRequestData } from '../types/AwayMessageCampaignRequestData';
import { CampaignExternalType, CampaignListItem, CampaignType, GetCampaignsParams, ScheduledType } from '../types';
import { MessageType } from 'src/constants/types';

export const useCampaignService = () => {
  const dispatch = useDispatch();
  const { loading, error } = useAppSelector(state => state.campaigns);

  const api = useCampaignsApi();

  const activateCampaign = useCallback(async (companyId: string, data: string[]) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ activate: true }));

      await api.activateCampaign(companyId, data);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ activate: false }));
    }
  }, []);

  const deactivateCampaign = useCallback(async (companyId: string, data: string[]) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ deactivate: true }));

      await api.deactivateCampaign(companyId, data);
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ deactivate: false }));
    }
  }, []);

  const startCampaign = useCallback(
    async (companyId: string, campaignId: string, contactIds: string[]) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ start: true }));

        await api.startCampaign(companyId, campaignId, contactIds);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ start: false }));
      }
    },
    [api],
  );

  const getAwayMessageCampaigns = useCallback(async (companyId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getAwayMessageCampaigns: true }));
      const awayMessageCampaignQuery: GetCampaignsParams = {
        companyId: companyId,
        limit: 2,
        offset: 0,
        external_type: 'custom,recurring',
        type: 'away_message',
        sort: 'date_updated_desc',
      };
      const awayMessageCampaigns = await api.getCampaigns(awayMessageCampaignQuery);
      const awayMessageCampaignItems = awayMessageCampaigns.data.items ?? [];
      const awayCustomMessageCampaign: CampaignListItem =
        awayMessageCampaignItems.find((item) => item.externalType === 'custom') ||
        ({
          campaignType: 'away_message' as CampaignType,
          externalType: 'custom' as CampaignExternalType,
          name: 'Away Message Campaign',
          scheduledMessages: [
            {
              text: '',
              type: 'sms' as MessageType,
              subject: '',
              links: [],
              position: 1,
              scheduledType: 'immediate' as ScheduledType,
            },
          ],
        } as unknown as CampaignListItem);
      const awayRecurringMessagesCampaign: CampaignListItem =
        awayMessageCampaignItems.find((item) => item.externalType === 'recurring') ||
        ({
          campaignType: 'away_message' as CampaignType,
          externalType: 'recurring' as CampaignExternalType,
          name: 'Away Message Campaign',
          scheduledMessages: [
            {
              text: '',
              type: 'sms' as MessageType,
              subject: '',
              links: [],
              position: 1,
              scheduledType: 'time_intervals' as ScheduledType,
              scheduledIntervals: [],
            },
          ],
        } as unknown as CampaignListItem);
      dispatch(
        setAwayCustomMessageCampaign({
          item: awayCustomMessageCampaign,
        }),
      );
      dispatch(
        setAwayRecurringMessagesCampaign({
          item: awayRecurringMessagesCampaign,
        }),
      );
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getAwayMessageCampaigns: false }));
    }
  }, []);

  const createAwayMessageCampaign = useCallback(
    async (companyId: string, data: AwayMessageCampaignRequestData): Promise<string> => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ createAwayMessageCampaign: true }));

        const id = await api.createAwayMessageCampaign(companyId, data);
        return id
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ createAwayMessageCampaign: false }));
      }
    },
    [],
  );

  const updateAwayMessageCampaign = useCallback(
    async (companyId: string, campaignId: string, data: AwayMessageCampaignRequestData) => {
      try {
        dispatch(resetError());
        dispatch(setLoading({ updateAwayMessageCampaign: true }));

        await api.updateAwayMessageCampaign(companyId, campaignId, data);
      } catch (e) {
        dispatch(setError(e));
        throw e;
      } finally {
        dispatch(setLoading({ updateAwayMessageCampaign: false }));
      }
    },
    [],
  );

  // Only decorates the optional contact capture offer, so a failure shows no toast and no page error.
  const getCampaignGroupNames = useCallback(async (companyId: string, campaignId: string) => {
    try {
      const { data } = await api.getCampaign(companyId, campaignId, { skipGenericToast: true });
      return (data.groups ?? []).map(group => group?.name).filter(Boolean);
    } catch {
      return [];
    }
  }, [api]);

  return {
    loading,
    error,
    activateCampaign,
    deactivateCampaign,
    startCampaign,
    getAwayMessageCampaigns,
    createAwayMessageCampaign,
    updateAwayMessageCampaign,
    getCampaignGroupNames,
  };
};
